import SafeUncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
} from 'reactstrap';
import { Organization } from 'types';
import { useCurrentOrganization } from 'utils/util/hooks';
import {
  AdvancedCurrentPeopleFilter,
  AdvancedCurrentPeopleFilterContextProvider,
} from 'views/Widgets/Filters/AdvancedSpecializedFilter';
import { TGroup } from 'views/Widgets/Filters/types';
import { OnFilterChange, ViewScope } from '../../../utils/models/Objective';
import { ObjectiveType } from '../ObjectiveTypeOperations';
import { RESET_ADVANCED_FILTERS_MARKER } from './hooks';
import ObjectivesOverviewCSVDownloader from './ObjectivesOverviewCSVDowloader';
import ObjectivesOverviewStatusFilter from './ObjectivesOverviewStatusFilter';
import ObjectivesOverviewTagFilter from './ObjectivesOverviewTagFilter';
import ObjectivesOverviewTextFilter from './ObjectivesOverviewTextFilter';
import ObjectivesOverviewTypeFilter from './ObjectivesOverviewTypeFilter';

type ConfigurationType = {
  value: ViewScope;
  text: React.ReactNode;
  popoverText?: React.ReactNode;
};

const configuration = (organization: Organization): ConfigurationType[] => [
  {
    value: 'everyone',
    text: (
      <FormattedMessage
        id="app.views.objectives.objectives_overview_page.scope.everyone"
        defaultMessage="All objectives"
      />
    ),
    popoverText: (
      <FormattedMessage
        id="app.views.objectives.objectives_overview_page.scope.everyone.popover"
        defaultMessage="The objectives of everyone at {orgName}"
        values={{ orgName: organization.name }}
      />
    ),
  },
  {
    value: 'everyone_visible',
    text: (
      <FormattedMessage
        id="app.views.objectives.objectives_overview_page.scope.everyone"
        defaultMessage="All objectives"
      />
    ),
    popoverText: (
      <FormattedMessage
        id="app.views.objectives.objectives_overview_page.scope.everyone_visible.popover"
        defaultMessage="The objectives of everyone you have visibility into at {orgName}"
        values={{ orgName: organization.name }}
      />
    ),
  },
  {
    value: 'my_full_team',
    text: (
      <FormattedMessage
        id="app.views.objectives.objectives_overview_page.scope.my_full_team"
        defaultMessage="My full team"
      />
    ),
  },
  {
    value: 'my_direct_reports',
    text: (
      <FormattedMessage
        id="app.views.objectives.objectives_overview_page.scope.my_direct_reports"
        defaultMessage="My direct reports"
      />
    ),
  },
  {
    value: 'my_objectives',
    text: (
      <FormattedMessage
        id="app.views.objectives.objectives_overview_page.scope.my_objectives"
        defaultMessage="My objectives"
      />
    ),
  },
];

type ObjectivesOverviewScopeFilterItem = {
  value: ViewScope;
  text: React.ReactNode;
  popoverText?: React.ReactNode;
  active: boolean;
  onChange: (delta: OnFilterChange) => void;
  disabled: boolean;
};

const ObjectivesOverviewScopeFilterItem = ({
  value,
  text,
  popoverText,
  active,
  onChange,
  disabled,
}: ObjectivesOverviewScopeFilterItem) => {
  // do nothing if we're already on this tab
  const onClickObjectivesOverviewScopeFilterItem = active
    ? undefined
    : (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
        onChange({ scope: (e.target as HTMLInputElement).value as ViewScope });

  return (
    <>
      <Button
        id={'scope-filter-item-' + value}
        size="sm"
        color="primary"
        active={active}
        disabled={disabled}
        onClick={onClickObjectivesOverviewScopeFilterItem}
        // we don't want to disable the button i.e. adopt the visuals
        // of a disabled button, but since clicking does nothing
        // if active, we want to show the cursor as default
        style={{ cursor: active ? 'default' : 'pointer' }}
        value={value}
        outline={!active}
      >
        {text}
      </Button>
      {!!popoverText && (
        <SafeUncontrolledPopover
          trigger="hover"
          placement="top"
          target={'scope-filter-item-' + value}
        >
          {popoverText}
        </SafeUncontrolledPopover>
      )}
    </>
  );
};

type Props = {
  disabled: boolean;
  scope?: ViewScope;
  peopleFilters?: TGroup;
  status: string[];
  objectiveTypeIds: number[];
  organizationObjectiveTypes: ObjectiveType[];
  availableScopes: ViewScope[];
  textFilter: string;
  tagNames: string[];
  firstDay: string;
  lastDay: string;
  onChange: (delta: OnFilterChange) => void;
  onGenerateCSV: () => void;
  onGenerateCSVCompleted: () => void;
  hasData: boolean;
};

const ObjectivesOverviewScopeFilter: FC<Props> = ({
  scope,
  status,
  objectiveTypeIds,
  organizationObjectiveTypes,
  peopleFilters,
  textFilter,
  tagNames,
  onChange,
  onGenerateCSV,
  onGenerateCSVCompleted,
  disabled,
  availableScopes,
  firstDay,
  lastDay,
  hasData,
}: Props) => {
  const { formatMessage } = useIntl();
  const currentOrganization = useCurrentOrganization();
  const filteredConfiguratons = useMemo(
    () =>
      configuration(currentOrganization).filter((item) =>
        availableScopes.includes(item.value)
      ),
    [availableScopes, currentOrganization]
  );
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    () =>
      status.length > 0 ||
      !!peopleFilters ||
      textFilter.length > 0 ||
      tagNames.length > 0 ||
      objectiveTypeIds.length > 0
  );

  const adaptAdvancedFilterOnChange = useCallback(
    (filters: TGroup | undefined) => {
      // Passing RESET_ADVANCED_FILTERS_MARKER alias of null (not undefined)
      // to distinguish the action of clearing the filter
      onChange({ peopleFilters: filters ?? RESET_ADVANCED_FILTERS_MARKER });
    },
    [onChange]
  );
  return (
    <Card>
      <CardHeader style={{ height: 'auto' }}>
        <Row className="justify-content-between">
          <Col className="col-12 col-lg-auto">
            <Row>
              <Col className="px-0 col-lg-auto">
                <ButtonGroup>
                  {filteredConfiguratons.map((item) => (
                    <ObjectivesOverviewScopeFilterItem
                      disabled={disabled}
                      key={'scope-filter-item-' + item.value}
                      onChange={onChange}
                      popoverText={item.popoverText}
                      value={item.value}
                      text={item.text}
                      active={item.value === scope}
                    />
                  ))}
                </ButtonGroup>
              </Col>
              <Col className="col-auto">
                <Button
                  disabled={disabled}
                  color="light"
                  className="btn-sm"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                >
                  <i
                    className="fe fe-filter"
                    style={{ position: 'relative', top: '1px' }}
                  />{' '}
                  {showAdvancedFilters ? (
                    <FormattedMessage
                      id="app.widgets.task.hide_filters_button_text"
                      defaultMessage="Hide filters"
                    />
                  ) : (
                    <FormattedMessage
                      id="app.widgets.task.toggle_filters_button_text"
                      defaultMessage="Filter this list"
                    />
                  )}
                </Button>
              </Col>
            </Row>
          </Col>
          {!!scope && (
            <Col className="col-12 col-lg-auto pe-0">
              <ObjectivesOverviewCSVDownloader
                disabled={disabled || !hasData}
                scope={scope}
                status={status}
                peopleFilters={peopleFilters}
                textFilter={textFilter}
                tagNames={tagNames}
                objectiveTypeIds={objectiveTypeIds}
                firstDay={firstDay}
                lastDay={lastDay}
                onGenerateCSV={onGenerateCSV}
                onGenerateCSVCompleted={onGenerateCSVCompleted}
              />
            </Col>
          )}
        </Row>
      </CardHeader>
      {showAdvancedFilters && (
        <CardBody className="p-3 mb-n3">
          <Row>
            <Col className="col-12 col-xl-6 mb-3">
              <ObjectivesOverviewStatusFilter
                disabled={disabled}
                value={status}
                onChange={onChange}
              />
            </Col>
            <Col className="col-12 col-xl-6 mb-3">
              <ObjectivesOverviewTypeFilter
                disabled={disabled}
                value={objectiveTypeIds}
                objectiveTypes={organizationObjectiveTypes}
                onChange={onChange}
              />
            </Col>
            <Col className="col-12 col-xl-6 mb-3">
              <ObjectivesOverviewTextFilter
                disabled={disabled}
                onChange={onChange}
              />
            </Col>
            <Col className="col-12 col-xl-6 mb-3">
              <ObjectivesOverviewTagFilter
                disabled={disabled}
                value={tagNames}
                onChange={onChange}
              />
            </Col>
            <Col className="col-12 col-xl-6 mb-3">
              <AdvancedCurrentPeopleFilterContextProvider roleScope="USER">
                <AdvancedCurrentPeopleFilter
                  value={peopleFilters}
                  onChange={adaptAdvancedFilterOnChange}
                  searchBarPlaceholder={formatMessage({
                    id: 'app.views.objectives.objectives_overview.objectives_overview_scope_filter.advanced.search_bar_placeholder',
                    defaultMessage: 'Filter by collaborators',
                  })}
                />
              </AdvancedCurrentPeopleFilterContextProvider>
            </Col>
          </Row>
        </CardBody>
      )}
    </Card>
  );
};

export default ObjectivesOverviewScopeFilter;
